import { type ReactNode } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Flag, SwapHoriz } from '@mui/icons-material';
import { Attendee, CheckinDate, Group } from '../../lib/types';
import { formatGroupShort } from '../../lib/groups';
import { flagDetail, flagOn } from '../../lib/attendee-flags';
import { formatDobWithAge } from '../../lib/age';
import { formatShirtSize } from '../../lib/shirt-size';
import { useFirebaseContext } from '../../lib/firebase-context';
import GroupPreferenceIcon from './group-preference-icon';
import GroupPreferenceNoteEditor from './group-preference-note-editor';

type CheckInStatus = 'here' | 'left' | 'absent';

const checkInStatusFor = (
  attendee: Attendee,
  date: CheckinDate | undefined
): CheckInStatus => {
  if (!date) return 'absent';
  if (date.checkedOutRefs?.includes(attendee.ref)) return 'left';
  if (date.checkedInRefs?.includes(attendee.ref)) return 'here';
  return 'absent';
};

const StatusCell = ({ status }: { status: CheckInStatus }) => {
  if (status === 'here') {
    return <Chip label='Here' size='small' color='success' />;
  }
  if (status === 'left') {
    return <Chip label='Left' size='small' color='default' />;
  }
  return (
    <Typography variant='body2' color='text.secondary'>
      —
    </Typography>
  );
};

const EmergencyContactCell = ({ attendee }: { attendee: Attendee }) => {
  const name = attendee.emergency_contact_name?.trim() || '';
  const phone = attendee.emergency_contact_phone?.trim() || '';
  const relationship = attendee.emergency_contact_relationship?.trim() || '';
  if (!name && !phone) return null;
  const nameLine = relationship ? `${name} (${relationship})` : name;
  return (
    <Stack spacing={0.25}>
      {nameLine && <Typography variant='body2'>{nameLine}</Typography>}
      {phone && (
        <Typography variant='caption' color='text.secondary'>
          {phone}
        </Typography>
      )}
    </Stack>
  );
};

// Chip with an optional hover tooltip carrying the detail text. Mirrors the
// pattern used in `attendees-list.tsx` so the Roster's screen view exposes
// the same hover behavior as the Check-in page. The `color` prop lets us
// render health-related chips (FA / MC) in warning-orange and logistical
// chips (RU = needs Russian lesson) in info-blue, so volunteers can
// distinguish them at a glance.
const FlagChip = ({
  label,
  on,
  detail,
  color = 'warning',
}: {
  label: 'FA' | 'MC' | 'RU';
  on: boolean;
  detail: string;
  color?: 'warning' | 'info';
}) => {
  if (!on) return null;
  const chip = (
    <Chip
      label={label}
      size='small'
      color={color}
      sx={{ fontSize: '0.7em', height: '20px' }}
    />
  );
  if (!detail) return chip;
  return (
    <Tooltip
      title={detail}
      placement='top'
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap' } } }}
    >
      <Box component='span' tabIndex={0} sx={{ display: 'inline-flex' }}>
        {chip}
      </Box>
    </Tooltip>
  );
};

// Red flag rendered inline next to the attendee's name when a note is
// present, mirroring the indicator on the Check-in page (see
// `attendees-list.tsx`). Hidden in print: the icon alone would lose its
// tooltip on paper, and the note text is surfaced in the print block
// (`NamePrintDetails`) instead.
const NotesFlag = ({ attendee }: { attendee: Attendee }) => {
  const note = attendee.notes?.trim();
  if (!note) return null;
  return (
    <Tooltip
      title={attendee.notes}
      placement='top'
      slotProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap' } } }}
    >
      <Box
        component='span'
        tabIndex={0}
        sx={{
          display: 'inline-flex',
          '@media print': { display: 'none' },
        }}
      >
        <Flag
          fontSize='small'
          sx={{ height: '0.8em', width: '0.8em' }}
          color='error'
        />
      </Box>
    </Tooltip>
  );
};

const FlagsCell = ({ attendee }: { attendee: Attendee }) => {
  const { customFields } = useFirebaseContext();
  const hasFA = flagOn(attendee.food_allergies);
  const hasMC = flagOn(attendee.medical_concerns);
  const hasRU = Boolean(attendee.needs_russian);
  const hasGP = Boolean(attendee.group_preference);
  const sortedCustomFields = customFields
    .slice()
    .sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
  const customChips = sortedCustomFields
    .map((f) => {
      const value = attendee.customFields?.[f.ref];
      if (!value) return null;
      return (
        <Tooltip key={f.ref} title={f.name} placement='top'>
          <Chip
            label={value}
            size='small'
            variant='outlined'
            sx={{ fontSize: '0.7em', height: '20px' }}
          />
        </Tooltip>
      );
    })
    .filter(Boolean);
  if (!hasFA && !hasMC && !hasRU && !hasGP && customChips.length === 0)
    return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <GroupPreferenceIcon attendee={attendee} />
      <GroupPreferenceNoteEditor attendee={attendee} />
      <FlagChip
        label='FA'
        on={hasFA}
        detail={flagDetail(attendee.food_allergies)}
      />
      <FlagChip
        label='MC'
        on={hasMC}
        detail={flagDetail(attendee.medical_concerns)}
      />
      <FlagChip
        label='RU'
        on={hasRU}
        detail='Needs lesson in Russian'
        color='info'
      />
      {customChips}
    </Box>
  );
};

// Italic detail lines (*FA: …, *MC: …, *Needs lesson in Russian)
// rendered as block elements directly under the attendee's name inside
// the Name cell. Print-only — on screen the same info is exposed via
// the Tooltip on each chip in the Flags column. Anchoring the lines to
// the Name cell keeps them visually attached to the kid they belong to
// even when other cells (most notably the multi-line Emergency contact
// cell) make the row tall.
const NamePrintDetails = ({ attendee }: { attendee: Attendee }) => {
  const faDetail = flagDetail(attendee.food_allergies);
  const mcDetail = flagDetail(attendee.medical_concerns);
  const needsRussian = Boolean(attendee.needs_russian);
  if (!faDetail && !mcDetail && !needsRussian) return null;
  return (
    <Box
      sx={{
        display: 'none',
        '@media print': { display: 'block', mt: 0.25 },
      }}
    >
      {faDetail && (
        <Typography
          variant='caption'
          sx={{
            fontStyle: 'italic',
            whiteSpace: 'pre-wrap',
            display: 'block',
          }}
        >
          *FA: {faDetail}
        </Typography>
      )}
      {mcDetail && (
        <Typography
          variant='caption'
          sx={{
            fontStyle: 'italic',
            whiteSpace: 'pre-wrap',
            display: 'block',
          }}
        >
          *MC: {mcDetail}
        </Typography>
      )}
      {needsRussian && (
        <Typography
          variant='caption'
          sx={{
            fontStyle: 'italic',
            whiteSpace: 'pre-wrap',
            display: 'block',
          }}
        >
          *Needs lesson in Russian
        </Typography>
      )}
    </Box>
  );
};

const GroupRosterCard = ({
  title,
  subtitle,
  attendees,
  showCheckInStatus = false,
  showGradeCompleted = false,
  showEmergencyContact = true,
  checkInDate,
  onMoveAttendee,
  onSelectAttendee,
  headerAction,
}: {
  title: string;
  subtitle?: string;
  attendees: Attendee[];
  showCheckInStatus?: boolean;
  showGradeCompleted?: boolean;
  // Emergency contact column is the slack column when shown. When
  // hidden, its width is folded into the Name column so the table
  // still fills the page.
  showEmergencyContact?: boolean;
  checkInDate?: CheckinDate;
  onMoveAttendee?: (attendee: Attendee) => void;
  // When provided, the Name cell becomes a clickable text-button that
  // calls the handler — typically used to open the attendee edit dialog,
  // mirroring the behavior on the Check-in page's attendee row.
  onSelectAttendee?: (attendee: Attendee) => void;
  headerAction?: ReactNode;
}) => {
  const count = attendees.length;
  const breakdown = showCheckInStatus
    ? attendees.reduce(
        (acc, a) => {
          const s = checkInStatusFor(a, checkInDate);
          acc[s] += 1;
          return acc;
        },
        { here: 0, left: 0, absent: 0 } as Record<CheckInStatus, number>
      )
    : null;
  // `gender` is VBS-owned and absent on manually-added attendees; surface
  // that gap as "n unknown" only when it's non-zero so all-synced groups
  // stay tidy.
  const genderBreakdown = attendees.reduce(
    (acc, a) => {
      if (a.gender === 'Male') acc.male += 1;
      else if (a.gender === 'Female') acc.female += 1;
      else acc.unknown += 1;
      return acc;
    },
    { male: 0, female: 0, unknown: 0 }
  );

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            mb: subtitle ? 0.5 : 1,
          }}
        >
          <Typography variant='h6'>{title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {headerAction}
            <Typography variant='body2' color='text.secondary'>
              {count} attendee{count === 1 ? '' : 's'}
              {breakdown && (
                <>
                  {' · '}
                  {breakdown.here} here {' · '}
                  {breakdown.left} left {' · '}
                  {breakdown.absent} absent
                </>
              )}
              {' · '}
              {genderBreakdown.male} male {' · '}
              {genderBreakdown.female} female
              {genderBreakdown.unknown > 0 && (
                <>
                  {' · '}
                  {genderBreakdown.unknown} unknown
                </>
              )}
            </Typography>
          </Box>
        </Box>
        {subtitle && (
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        {count === 0 ? (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ fontStyle: 'italic', mt: 1 }}
          >
            No attendees assigned yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table
              size='small'
              sx={{
                // With EC shown, we anchor every column to a fixed
                // percentage (`table-layout: fixed`) so the Roster's
                // many cards stay vertically aligned with each other —
                // EC absorbs the remaining width as a slack column.
                // With EC hidden, percentage widths would either
                // over-allocate to one column (the old "fold all slack
                // into Name" strategy looked unbalanced) or require
                // recomputing every other column. Letting the browser
                // size columns by content (`auto`) gives each column
                // only what its data needs and lets the rest of the
                // row breathe — matching the strategy the print
                // stylesheet already uses.
                tableLayout: showEmergencyContact ? 'fixed' : 'auto',
                '& tbody tr:last-child td': { borderBottom: 0 },
              }}
            >
              {/*
                Column order: Name | Gender | DOB | Shirt | (GC) |
                Flags | (EC) | (Status) | (Move). The colgroup is only
                rendered when EC is shown — that's the only case where
                we use `table-layout: fixed`. When EC is hidden the
                table is `auto`-layout, so explicit widths would just
                act as preferred hints and conflict with content-based
                sizing.

                Fixed columns when EC shown: Gender 5 + DOB 15 + Shirt
                10 + Flags 12 = 42. EC starts as the slack column
                (target Name 25 + slack 33 = 58 on top of those 42),
                but it's floored at EC_MIN so it stays wide enough to
                fit a 'First Last (Relationship)' line on one row when
                every optional column is on. Whenever the floor kicks
                in, the deficit comes out of the Name column instead —
                Name wraps gracefully thanks to the button cell's
                `overflowWrap: 'anywhere'`. Shirt is 10% (not 7% like
                GC) because the widest recognised value 'Adult 2XL' is
                9 chars and needs more breathing room than the 2-char
                'GC'.
              */}
              {showEmergencyContact &&
                (() => {
                  let ec = 33;
                  if (showGradeCompleted) ec -= 7;
                  if (showCheckInStatus) ec -= 10;
                  if (onMoveAttendee) ec -= 5;
                  // Lower bound for EC so contact lines don't wrap into
                  // a tall stack of single words when every optional
                  // column is on. 22% fits "First Last (Relationship)"
                  // for typical names at typical container widths and
                  // leaves the phone on its own caption row.
                  const EC_MIN = 22;
                  let nameWidth = 25;
                  if (ec < EC_MIN) {
                    nameWidth -= EC_MIN - ec;
                    ec = EC_MIN;
                  }
                  return (
                    <colgroup>
                      <col style={{ width: `${nameWidth}%` }} />
                      <col style={{ width: '5%' }} />
                      <col style={{ width: '15%' }} />
                      <col style={{ width: '10%' }} />
                      {showGradeCompleted && (
                        <col style={{ width: '7%' }} />
                      )}
                      <col style={{ width: '12%' }} />
                      <col style={{ width: `${ec}%` }} />
                      {showCheckInStatus && (
                        <col style={{ width: '10%' }} />
                      )}
                      {onMoveAttendee && <col style={{ width: '5%' }} />}
                    </colgroup>
                  );
                })()}
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Gender</TableCell>
                  <TableCell>DOB</TableCell>
                  <TableCell>
                    <Tooltip title='T-shirt size' placement='top'>
                      <Box component='span'>Shirt</Box>
                    </Tooltip>
                  </TableCell>
                  {showGradeCompleted && (
                    <TableCell>
                      <Tooltip title='Grade completed' placement='top'>
                        <Box component='span'>GC</Box>
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell>Flags</TableCell>
                  {showEmergencyContact && (
                    <TableCell>Emergency contact</TableCell>
                  )}
                  {showCheckInStatus && <TableCell>Status</TableCell>}
                  {onMoveAttendee && (
                    <TableCell align='right' data-print-hide='true'>
                      Move
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {attendees.map((a) => {
                  return (
                    <TableRow
                      key={a.ref}
                      hover
                      sx={{ height: 56 }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          {onSelectAttendee ? (
                            <Button
                              variant='text'
                              type='button'
                              size='small'
                              onClick={() => onSelectAttendee(a)}
                              sx={{
                                // Strip MUI Button chrome that would
                                // otherwise add row height: no padding,
                                // no min-width, inherit the table's font.
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                whiteSpace: 'normal',
                                overflowWrap: 'anywhere',
                                lineHeight: 1.25,
                                p: 0,
                                minWidth: 0,
                                fontSize: 'inherit',
                                fontWeight: 'inherit',
                                // Link-style color + underline-on-hover
                                // is the click affordance. The print
                                // rules in `groups-roster.tsx` force
                                // every color to black, so the PDF
                                // renders as plain text and hover state
                                // never applies in print.
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  textDecoration: 'underline',
                                },
                              }}
                            >
                              {a.last_name}, {a.first_name}
                            </Button>
                          ) : (
                            <Box component='span'>
                              {a.last_name}, {a.first_name}
                            </Box>
                          )}
                          <NotesFlag attendee={a} />
                        </Box>
                        <NamePrintDetails attendee={a} />
                      </TableCell>
                      <TableCell>
                        {a.gender ? (
                          <Tooltip title={a.gender} placement='top'>
                            <Box component='span'>
                              {a.gender.charAt(0)}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDobWithAge(a.birthdate)}</TableCell>
                      <TableCell>
                        {a.tshirt_size ? (
                          <Tooltip
                            title={a.tshirt_size}
                            placement='top'
                          >
                            <Box component='span'>
                              {formatShirtSize(a.tshirt_size)}
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography
                            variant='body2'
                            color='text.secondary'
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                      {showGradeCompleted && (
                        <TableCell>
                          {a.grade_completed ? (
                            a.grade_completed
                          ) : (
                            <Typography
                              variant='body2'
                              color='text.secondary'
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        <FlagsCell attendee={a} />
                      </TableCell>
                      {showEmergencyContact && (
                        <TableCell>
                          <EmergencyContactCell attendee={a} />
                        </TableCell>
                      )}
                      {showCheckInStatus && (
                        <TableCell>
                          <StatusCell
                            status={checkInStatusFor(a, checkInDate)}
                          />
                        </TableCell>
                      )}
                      {onMoveAttendee && (
                        <TableCell align='right' data-print-hide='true'>
                          <Tooltip title='Move to another group'>
                            <IconButton
                              size='small'
                              onClick={() => onMoveAttendee(a)}
                            >
                              <SwapHoriz fontSize='small' />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// Helper for callers: build a group title from a Group document.
export const groupCardTitle = (group: Group): string => formatGroupShort(group);

export default GroupRosterCard;
